/**
 * Módulo de Cambio de Contraseña.
 * Proporciona una interfaz segura para que los usuarios (Administradores, Laboratorios e Investigadores)
 * actualicen sus credenciales de acceso, validando la fortaleza de la clave y la coincidencia de campos.
 */

import React, { useState } from "react";
import axios from "axios";
import { Apiurl } from "../../shared/services/apirest";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

const DashboardCambiarPassword = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [toast, setToast] = useState({
    show: false,
    msg: "",
    type: "",
  });

  const [loading, setLoading] = useState(false);

  /**
   * Procesa el envío del formulario para cambiar la contraseña.
   * Realiza validaciones previas en el cliente antes de llamar a la API.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validación de coincidencia de contraseñas
    if (form.new_password !== form.confirm_password) {
      setToast({
        show: true,
        msg: "❌ Las contraseñas nuevas no coinciden.",
        type: "danger",
      });
      setLoading(false);
      return;
    }

    // Validación de longitud mínima
    if (form.new_password.length < 8) {
      setToast({
        show: true,
        msg: "❌ La nueva contraseña debe tener al menos 8 caracteres.",
        type: "danger",
      });
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const res = await axios.post(`${Apiurl}users/change_password/`, {
        old_password: form.old_password,
        new_password: form.new_password,
        confirm_password: form.confirm_password,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setToast({
        show: true,
        msg: res.data.detail || "✅ Tu contraseña ha sido cambiada correctamente.",
        type: "success",
      });

      // Limpiar el formulario tras el éxito
      setForm({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });

    } catch (err) {
      console.error("❌ Error backend:", err.response?.data);
      let errorMsg = "❌ Error al cambiar la contraseña. Verifica tus datos actuales.";

      // Manejo de errores específicos del servidor
      if (err.response?.data) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = `❌ ${err.response.data.detail}`;
        } else if (err.response.data.new_password) {
          errorMsg = `❌ Nueva contraseña: ${err.response.data.new_password.join(" ")}`;
        } else if (err.response.data.old_password) {
          errorMsg = `❌ Contraseña actual: ${err.response.data.old_password.join(" ")}`;
        }
      }

      setToast({
        show: true,
        msg: errorMsg,
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gestiona la navegación de retorno según el rol del usuario.
   */
  const handleBack = () => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const role = userData.role;

    if (role === 'admin' || role === 'admin_lab' || role === 'admin_investigacion') {
      navigate("/DashboardAdmin");
    } else if (role === 'laboratorio') {
      navigate("/GestionLab");
    } else if (role === 'investigador') {
      navigate("/DashboardInvestigador");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light">
      <div className="card border-0 shadow-lg" style={{ maxWidth: "450px", width: "100%", borderRadius: "20px" }}>
        <div className="card-body p-5">
          <div className="text-center mb-4">
            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: "80px", height: "80px" }}>
              <i className="bi bi-shield-lock text-primary fs-1"></i>
            </div>
            <h2 className="fw-bold text-dark">Actualizar Seguridad</h2>
            <p className="text-muted small">Renueve su contraseña periódicamente para proteger su cuenta institucional.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label small fw-bold text-secondary">Contraseña Actual</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0"><i className="bi bi-key text-muted"></i></span>
                <input
                  type="password"
                  placeholder="Ingrese su contraseña actual"
                  className="form-control border-start-0 ps-0"
                  value={form.old_password}
                  onChange={(e) => setForm({ ...form, old_password: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label small fw-bold text-secondary">Nueva Contraseña</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0"><i className="bi bi-lock text-muted"></i></span>
                <input
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  className="form-control border-start-0 ps-0"
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label small fw-bold text-secondary">Confirmar Nueva Contraseña</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0"><i className="bi bi-check2-circle text-muted"></i></span>
                <input
                  type="password"
                  placeholder="Repita su nueva contraseña"
                  className="form-control border-start-0 ps-0"
                  value={form.confirm_password}
                  onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100 py-2 fw-bold shadow-sm mb-3"
              disabled={loading}
              style={{ borderRadius: "10px" }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Procesando...
                </>
              ) : "Actualizar Contraseña"}
            </button>
          </form>

          {/* Notificación de resultado (Éxito/Error) */}
          {toast.show && (
            <div className={`alert alert-${toast.type} py-2 small text-center fw-bold animate__animated animate__fadeIn`}>
              {toast.msg}
            </div>
          )}

          <div className="text-center">
            <button
              className="btn btn-link link-secondary text-decoration-none small fw-bold"
              onClick={handleBack}
            >
              <i className="bi bi-arrow-left me-1"></i> Volver al Panel de Control
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCambiarPassword;
